import React, { useCallback, useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Typography, CircularProgress, Collapse } from '@mui/material';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import {
  CollectorInstallError,
  Maybe,
  useCollectorSettingLazyQuery
} from 'graphql/types';
import { useRetryInstallationFlow } from '../hooks/useRetryCollectorInstallationFlow';
import { useCollectorSyncStatusContext } from '../contexts/CollectorSyncStatusContextProvider';
import { SyncStatus } from '../utils';
import { CollectorSyncing } from './CompanyCollectorSettingsDialog/CollectorSyncing';

type CollectorErrorListUnifiedProps = {
  errors: Array<CollectorInstallError | null | undefined> | undefined | null;
  installId: string;
  retryURL?: Maybe<string>;
};

const useStyles = makeStyles((theme) => ({
  itemContainer: {
    minWidth: '500px',
    paddingLeft: theme.spacing(4.5),
    paddingBottom: theme.spacing(0),
    margin: theme.spacing(0, 1)
  },
  listContainer: {
    maxHeight: '240px',
    padding: theme.spacing(0, 2, 1.5, 2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    marginBottom: theme.spacing(1.5)
  },
  errorContainer: {
    display: 'flex',
    color: theme.palette.error.dark,
    padding: theme.spacing(1.3, 2, 0.5, 2),
    margin: theme.spacing(2, 0, 0, 0),
    backgroundColor: theme.palette.error.light,
    alignItems: 'center'
  },

  errorBanner: {
    fontWeight: '600'
  },
  retryButtonContainer: {
    flexShrink: 0,
    alignItems: 'center'
  },
  retryButton: {
    minWidth: 'fit-content',
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    padding: '0px',
    '&:hover': {
      backgroundColor: theme.palette.error.light
    },
    ...theme.typography.caption
  },
  retryIndicator: {
    verticalAlign: 'middle',
    marginLeft: theme.spacing(1),
    color: theme.palette.error.dark
  },
  outerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  title: {
    marginTop: theme.spacing(1)
  },
  dataValue: {
    display: 'block'
  },
  icon: {
    color: theme.palette.common.black
  },
  li: {
    display: 'flex',
    alignItems: 'center'
  }
}));

export const CollectorErrorListUnified: React.FC<CollectorErrorListUnifiedProps> = ({
  errors,
  retryURL,
  installId
}: CollectorErrorListUnifiedProps) => {
  const [open, setOpen] = React.useState(true);
  const handleClick = () => {
    setOpen(!open);
  };
  const { retryInstallationFlow } = useRetryInstallationFlow();
  const [getSettings, { data, loading }] = useCollectorSettingLazyQuery();
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [collectorErrors, setCollectorErrors] = useState<
    Array<CollectorInstallError | null | undefined> | undefined | null
  >(errors);
  const {
    collectorsSyncStatus,
    setCollectorSyncStatus
  } = useCollectorSyncStatusContext();

  const [isSyncing, setIsSyncing] = useState<boolean>(
    collectorsSyncStatus[installId] === SyncStatus.inProgress
  );

  useEffect(() => {
    if (collectorsSyncStatus[installId] === SyncStatus.inProgress)
      setIsSyncing(true);
    else {
      getSettings({
        variables: { input: { installId } }
      });
    }
  }, [getSettings, installId, collectorsSyncStatus]);

  useEffect(() => {
    if (!loading && data?.collectorSetting?.item?.installStatus?.errors) {
      const errors = data.collectorSetting.item.installStatus.errors;
      setCollectorErrors(errors);

      setIsSyncing(false);
      setIsRetrying(false);
    }
  }, [
    data?.collectorSetting?.item?.installStatus?.errors,
    setCollectorErrors,
    setIsSyncing,
    setIsRetrying,
    loading
  ]);

  const onRetrySuccess = useCallback(() => {
    setCollectorSyncStatus(installId, SyncStatus.inProgress);
    setCollectorErrors(null);
  }, [setCollectorSyncStatus, setCollectorErrors, installId]);

  const onRetryClicked = useCallback(async () => {
    setIsRetrying(true);
    if (retryURL) {
      try {
        const dataSet = await retryInstallationFlow({
          retryURL
        });

        dataSet?.data?.retryInstallationFlow?.status === 200
          ? onRetrySuccess()
          : setIsRetrying(false);
      } catch (e: any) {
        console.error(e.message);
        setIsRetrying(false);
      }
    }
  }, [retryInstallationFlow, retryURL, onRetrySuccess]);

  const classes = useStyles();

  if (!isSyncing && (!collectorErrors || collectorErrors.length < 1)) {
    return null;
  }

  return (
    <div
      className={classes.outerContainer}
      data-testid="setting-dialog-CollectorErrorListUnified"
    >
      {isSyncing ? (
        <CollectorSyncing />
      ) : (
        collectorErrors &&
        collectorErrors.length > 0 && (
          <>
            <div className={classes.errorContainer} onClick={handleClick}>
              {open ? <ExpandLess className={classes.icon} /> : <ExpandMore />}
              <Typography
                className={classes.errorBanner}
                data-testid="errorBanner"
                align="left"
                variant="body2"
              >
                <FormattedMessage
                  id={'collectorSetting.errors'}
                  values={{ number: collectorErrors.length }}
                />
              </Typography>
            </div>
            <Collapse
              in={open}
              timeout="auto"
              unmountOnExit
              className={classnames(classes.listContainer)}
            >
              <div>
                {collectorErrors.map((data, index) => {
                  return (
                    <ul key={index} className={classes.itemContainer}>
                      <li>
                        <div className={classes.li}>
                          <Typography
                            data-testid="errorMessage"
                            variant="caption"
                            className={classes.dataValue}
                          >
                            {data?.message}
                          </Typography>
                          {retryURL && (
                            <span className={classes.retryButtonContainer}>
                              <span>&nbsp; - </span>
                              {isRetrying && (
                                <CircularProgress
                                  data-testid="progressIndicator"
                                  className={classes.retryIndicator}
                                  size={14}
                                />
                              )}
                              <Button
                                data-testid="retryButton"
                                onClick={onRetryClicked}
                                disabled={isRetrying}
                                className={classes.retryButton}
                              >
                                <FormattedMessage
                                  id={'collectorSetting.details.retry'}
                                />
                              </Button>
                            </span>
                          )}
                        </div>
                      </li>
                    </ul>
                  );
                })}
              </div>
            </Collapse>
          </>
        )
      )}
    </div>
  );
};
